import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Dropdown, Form, Modal, Spinner } from 'react-bootstrap';
import Avatar from 'components/common/Avatar';
import { toast } from 'react-toastify';
import {
  isSelectiveAssociation,
  useListNotaryAssociationsQuery,
  useShareJournalEntriesMutation
} from 'rtk/notary-share.slice';

const getAdminParts = item => {
  const profile = item?.Profile || {};
  const org = profile.OrganizationName?.trim();
  const name = profile.FullName?.trim();
  const email = (profile.Email || item?.Email || '').trim();
  const title = org || name || email || 'Unknown';
  return {
    title,
    email: email && email.toLowerCase() !== title.toLowerCase() ? email : ''
  };
};

const AdminOption = ({ item }) => {
  const { title, email } = getAdminParts(item);
  return (
    <>
      <Avatar
        size="xl"
        name={title}
        className="me-2 flex-shrink-0"
        mediaClass="bg-soft-primary text-primary"
      />
      <div className="lh-sm text-truncate">
        <div className="fw-semi-bold fs--1 text-truncate">{title}</div>
        {email ? (
          <div className="fs--2 opacity-75 text-truncate">{email}</div>
        ) : null}
      </div>
    </>
  );
};

AdminOption.propTypes = {
  item: PropTypes.object.isRequired
};

const ShareJournalModal = ({ show, onHide, entryGuids, entryCount }) => {
  const {
    data: associationsRes,
    isFetching,
    isLoading,
    isError,
    error
  } = useListNotaryAssociationsQuery(
    { skip: 0, maxResults: 100 },
    { skip: !show }
  );

  const [shareJournalEntries, { isLoading: sharing }] =
    useShareJournalEntriesMutation();

  const selectiveAdmins = useMemo(() => {
    const objects = Array.isArray(associationsRes?.Objects)
      ? associationsRes.Objects
      : [];
    return objects.filter(isSelectiveAssociation);
  }, [associationsRes]);

  const [adminGuid, setAdminGuid] = React.useState('');
  const selectedAdmin = selectiveAdmins.find(
    item => item?.Profile?.UserGuid === adminGuid
  );

  React.useEffect(() => {
    if (show) {
      setAdminGuid('');
    }
  }, [show]);

  const handleShare = async () => {
    if (!adminGuid || !entryGuids?.length) return;
    try {
      await shareJournalEntries({
        adminGuid,
        entryGuids
      }).unwrap();
      onHide(true);
    } catch (err) {
      const alreadyShared =
        String(err?.Code || err?.code || '').trim() === 'AlreadyExists' ||
        err?.statusCode === 409 ||
        err?.status === 409;
      const message =
        (typeof err?.Data === 'string' && err.Data.trim()) ||
        err?.Message ||
        err?.Text ||
        (alreadyShared
          ? 'This journal entry has already been shared with this organization.'
          : 'Failed to share journal entries.');
      const toastFn = alreadyShared ? toast.warning : toast.error;
      toastFn(message, {
        theme: 'colored',
        position: 'bottom-center'
      });
    }
  };

  const loading = isFetching || isLoading;
  const listError = isError
    ? error?.Message || error?.Text || 'Failed to load organizations.'
    : null;

  return (
    <Modal show={show} onHide={() => !sharing && onHide(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Share with organization</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Share {entryCount} journal {entryCount === 1 ? 'entry' : 'entries'}{' '}
          with an organization admin that has selective access.
        </p>

        {loading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span className="text-600 fs--1">Loading organizations…</span>
          </div>
        ) : null}

        {listError ? (
          <Alert variant="danger" className="fs--1 mb-3">
            {listError}
          </Alert>
        ) : null}

        {!loading && !listError && selectiveAdmins.length === 0 ? (
          <Alert variant="warning" className="fs--1 mb-0">
            No organizations with selective access are available. An accepted
            association with selective sharing is required before you can share
            journal entries.
          </Alert>
        ) : null}

        {!loading && selectiveAdmins.length > 0 ? (
          <Form.Group>
            <Form.Label>
              Organization <span className="text-danger">*</span>
            </Form.Label>
            <Dropdown className="w-100">
              <Dropdown.Toggle
                variant="falcon-default"
                className="w-100 d-flex align-items-center justify-content-between text-start py-2"
                disabled={sharing}
                aria-label="Select organization"
              >
                <span className="d-flex align-items-center text-truncate me-2">
                  {selectedAdmin ? (
                    <AdminOption item={selectedAdmin} />
                  ) : (
                    <span className="text-500">Select organization</span>
                  )}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu
                className="w-100 py-1"
                style={{ maxHeight: 280, overflowY: 'auto' }}
              >
                {selectiveAdmins.map(item => {
                  const guid = item?.Profile?.UserGuid;
                  if (!guid) return null;
                  return (
                    <Dropdown.Item
                      key={guid}
                      active={guid === adminGuid}
                      onClick={() => setAdminGuid(guid)}
                      className="d-flex align-items-center py-2"
                    >
                      <AdminOption item={item} />
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => onHide(false)}
          disabled={sharing}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleShare}
          disabled={
            sharing ||
            loading ||
            !adminGuid ||
            !entryGuids?.length ||
            selectiveAdmins.length === 0
          }
        >
          {sharing ? (
            <Spinner animation="border" size="sm" className="me-1" />
          ) : null}
          Share
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ShareJournalModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  entryGuids: PropTypes.arrayOf(PropTypes.string).isRequired,
  entryCount: PropTypes.number.isRequired
};

export default ShareJournalModal;
